import clsx from 'clsx'
import type { LocalVideoTrack, RemoteTrack } from 'livekit-client'
import { useEffect, useRef } from 'react'
import { samplesToTc } from '../lib/timecode'
import '../styles/slp.css'

/**
 * Program feed viewer (rt UI v2 spec §画面传输 #2/#4 + v2.6 overlay/popout) —
 * the studio screen-share ("program") on remote ends, or the local preview on
 * the studio. Double-click (or the ⛶ button) toggles fullscreen; ↗ pops the
 * video out into its own window (dual-screen actors: script on one screen,
 * picture on the other). Overlays: timecode top-right (burn-in, same source
 * as rt-tc-display) + who's-watching roster top-left.
 *
 * `withTcTestid` controls which instance carries data-testid="rt-tc-display"
 * (exactly one per page — the multiuser e2e reads its textContent).
 */
export function ProgramFeed({
  track,
  tcSamples,
  fps,
  tcPlaying,
  placeholder = 'Waiting for program feed…',
  withTcTestid = false,
  roster,
  rec = false,
  withRecTestid = false,
  className,
}: {
  track: LocalVideoTrack | RemoteTrack | null
  tcSamples: number
  fps: number
  tcPlaying: boolean
  placeholder?: string
  withTcTestid?: boolean
  /** participant names overlaid top-left (v2.6 "在场名单") */
  roster?: string[]
  /** v2.7 #4 — REC red light: 3px red outline + ● REC badge while recording */
  rec?: boolean
  /** which instance carries data-testid="rt-rec-light" (the actor page keeps
   *  its big ON AIR block as the canonical rt-rec-light — frozen e2e) */
  withRecTestid?: boolean
  className?: string
}) {
  const hostRef = useRef<HTMLDivElement>(null)
  const videoHostRef = useRef<HTMLDivElement>(null)
  const popoutRef = useRef<Window | null>(null)

  useEffect(() => {
    const el = videoHostRef.current
    if (!el || !track) return
    const media = track.attach()
    media.style.width = '100%'
    media.style.height = '100%'
    media.style.objectFit = 'contain'
    el.appendChild(media)
    return () => {
      track.detach(media)
      media.remove()
    }
  }, [track])

  // keep an open popout fed with the current track; close it when we unmount
  useEffect(() => {
    const w = popoutRef.current
    if (!w || w.closed) return
    feedPopout(w, track)
  }, [track])
  useEffect(
    () => () => {
      const w = popoutRef.current
      if (w && !w.closed) w.close()
    },
    [],
  )

  const toggleFullscreen = () => {
    const host = hostRef.current
    if (!host) return
    if (document.fullscreenElement) {
      void document.exitFullscreen().catch(() => undefined)
    } else {
      void host.requestFullscreen().catch(() => undefined)
    }
  }

  const openPopout = () => {
    const existing = popoutRef.current
    if (existing && !existing.closed) {
      existing.focus()
      return
    }
    const w = window.open('', '_blank', 'width=960,height=580')
    if (!w) return
    popoutRef.current = w
    w.document.title = 'Program feed'
    w.document.body.style.cssText =
      'margin:0;background:#000;display:flex;align-items:center;justify-content:center;height:100vh;overflow:hidden'
    feedPopout(w, track)
  }

  return (
    <div
      ref={hostRef}
      data-testid="rt-program-feed"
      data-live={track ? '1' : '0'}
      onDoubleClick={toggleFullscreen}
      title="Double-click for fullscreen"
      className={clsx(
        'relative aspect-video w-full select-none overflow-hidden rounded-md bg-[#0b0b0c]',
        rec
          ? 'border-[3px] border-[var(--slp-lamp-red)]'
          : 'border border-[var(--slp-border)]',
        className,
      )}
    >
      <div ref={videoHostRef} className="absolute inset-0" />
      {!track ? (
        <div className="absolute inset-0 flex items-center justify-center text-[13px] text-white/45">
          {placeholder}
        </div>
      ) : null}
      <div className="absolute left-2 top-2 flex max-w-[60%] items-center gap-1.5">
        {rec ? (
          <span
            {...(withRecTestid ? { 'data-testid': 'rt-rec-light', 'data-state': 'recording' } : {})}
            className="animate-pulse rounded bg-[var(--slp-lamp-red)] px-2 py-0.5 text-[11px] font-black tracking-wider text-white"
          >
            ● REC
          </span>
        ) : null}
        {roster && roster.length > 0 ? (
          <span
            data-testid="rt-program-roster"
            className="truncate rounded bg-black/65 px-2 py-0.5 text-[11px] font-medium text-white"
            title={roster.join(', ')}
          >
            ● {roster.join(' · ')}
          </span>
        ) : null}
      </div>
      <div
        className="absolute right-2 top-2 rounded bg-black/65 px-2 py-0.5 font-mono text-[15px] tabular-nums tracking-widest text-white"
        {...(withTcTestid ? { 'data-testid': 'rt-tc-display', 'data-playing': tcPlaying || undefined } : {})}
      >
        {samplesToTc(tcSamples, fps)}
      </div>
      <div className="absolute bottom-2 right-2 flex gap-1">
        <button
          type="button"
          onClick={toggleFullscreen}
          title="Fullscreen"
          data-testid="rt-video-fullscreen"
          className="rounded bg-black/65 px-2 py-1 text-[13px] leading-none text-white hover:bg-black/85"
        >
          ⛶
        </button>
        <button
          type="button"
          onClick={openPopout}
          disabled={!track}
          title="Open the video in its own window"
          data-testid="rt-video-popout"
          className="rounded bg-black/65 px-2 py-1 text-[13px] leading-none text-white hover:bg-black/85 disabled:opacity-40"
        >
          ↗
        </button>
      </div>
    </div>
  )
}

/** (Re)attach the current track's MediaStream inside the popout window. */
function feedPopout(w: Window, track: LocalVideoTrack | RemoteTrack | null) {
  const doc = w.document
  doc.body.replaceChildren()
  const mst = track?.mediaStreamTrack
  if (!mst) {
    const note = doc.createElement('div')
    note.textContent = 'Waiting for program feed…'
    note.style.cssText = 'color:#777;font:13px sans-serif'
    doc.body.appendChild(note)
    return
  }
  const video = doc.createElement('video')
  video.autoplay = true
  video.muted = true
  video.playsInline = true
  video.style.cssText = 'width:100%;height:100%;object-fit:contain'
  video.srcObject = new MediaStream([mst])
  doc.body.appendChild(video)
  void video.play().catch(() => undefined)
}
